import { useEffect, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { UIContext } from '../../contexts/UIContext';

import { useGetPossibleVectorFocusForSelector } from '../../hooks/usePossibleVectorFocusService';
import { useGetDiseaseVectorsForSelector } from '../../hooks/useDiseaseVectorsService';
import { useGetDenounceStatusForSelector } from '../../hooks/useDenounceStatusService';
import { useGeoLocation } from '../../hooks/useGeoLocation';

import PicturesService from '../../services/PicturesService';
import GeoLocationService from '../../services/GeoLocationService';
import VectorFocusService from '../../services/VectorFocusService';
import AddressService from '../../services/AddressService';

import Header from '../../components/Header/Header';
import BottomMenu from '../../components/BottomMenu/BottomMenu';
import CameraCapture from '../../components/CameraCapture/CameraCapture';
import CreateVectorFocusForm from '../../components/Forms/CreateVectorFocusForm/CreateVectorFocusForm';
import AddressForm from '../../components/Forms/AddressForm/AddressForm';
import InformationDialogue from '../../components/InformationDialogue/InformationDialogue';

import './VectorFocusCreate.scss';

function VectorFocusCreate() {
  const navigate = useNavigate();
  
  const { authUser, isAuthorized } = useContext(AuthContext);
  const {
    INFO_DIALOGUE_MSGS,
    setOpenInfoDialogue,
    handleCloseInfoDialogue,
    openInfoDialogue,
    infoMsgTitle,
    infoMsgLabel,
    infoMsgContent,
    showErrorMessage
  } = useContext(UIContext);

  const [ latitude, longitude, geoLocationError ] = useGeoLocation();
  const [, diseaseVectors] = useGetDiseaseVectorsForSelector(true);
  const [, possibleVectorFocus] = useGetPossibleVectorFocusForSelector(true);
  const [, , initialDenounceStatus] = useGetDenounceStatusForSelector(true);

  const [vectorFocusImageFile, setVectorFocusImageFile] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [registeredVectorFocus, setRegisteredVectorFocus] = useState(null)

  const agentCity = authUser?.roles?.agent?.city?.name;

  useEffect(() => {
    if (!isAuthorized) navigate('/login');
  }, [isAuthorized, navigate]);

  useEffect(() => {
    if (geoLocationError) {
      showErrorMessage({
        message: `Houve uma falha ao obter sua geolocalização: ${geoLocationError}. Precisamos da geolocalização para criar um foco, se certifique que você concedeu a permissão para utilizar a geolocalização e tente novamente.`
      });
    }
  }, [geoLocationError, showErrorMessage])

  async function handleSubmitVectorFocusForm(values, { setSubmitting }) {
    const pictureService = new PicturesService();
    const resOpPicture = await pictureService.upsertPicture({
      pickedImgFile: vectorFocusImageFile,
      currentUserId: authUser?.user?.id
    });
    const picture = resOpPicture?.data;

    if (resOpPicture.status !== 1 || !picture) {
      showErrorMessage({
        message: "Houve uma falha ao enviar a foto.\nOs tipos válidos de arquivo são: jpg, jpeg, png.\nO tamanho máximo permitido é: 2mb"
      });
      setSubmitting(false);
      return;
    }

    const geoLocationService = new GeoLocationService();
    const resOpGeoLocation = await geoLocationService.createGeoLocation({
      creatorId: authUser?.user?.id,
      lat: latitude,
      lng: longitude,
      bounds: [],
      description: 'Foco de vetor de doença registrado por um agente'
    });
    const geoLocation = resOpGeoLocation?.data;

    if (resOpGeoLocation.status !== 1 || !geoLocation) {
      showErrorMessage({
        message: "Houve uma falha ao enviar a geolocalização"
      });
      setSubmitting(false);
      return;
    }

    const vectorFocusService = new VectorFocusService();
    const resCreateFocus = await vectorFocusService.createVectorFocus({
      userId: authUser.user.id,
      cityId: authUser.roles.agent.city_id,
      denounceStatusId: initialDenounceStatus.id,
      geoLocationId: geoLocation.id,
      pictureId: picture.id,
      description: values.description, 
      diseaseVectorId: values.diseaseVector?.id, 
      possibleFocusId: values.possibleFocus?.id
    });
    const newVectorFocus = resCreateFocus?.data;

    if (resCreateFocus.status !== 1 || !newVectorFocus) {
      showErrorMessage({
        message: "Não foi possível registrar o novo foco"
      });
      setSubmitting(false);
      return;
    }

    setRegisteredVectorFocus(newVectorFocus);
    setSubmitting(false);
    setShowAddressForm(true);
  }

  async function handleSubmitAddressForm(values, {setSubmitting }) {
    const addressService = new AddressService();
    const repUpsertAddress = await addressService.upsertAddress({
      countryId: authUser?.roles?.agent?.city?.country_id,
      stateId: authUser?.roles?.agent?.city?.state_id,
      cityId: authUser?.roles?.agent?.city?.id,
      neighborhood: values.neighborhood,
      street: values.street,
      number: values.number,
      zipCode: values.zipCode,
      additionalInfo: values.complement
    });
    const address = repUpsertAddress?.data;

    if (repUpsertAddress.status !== 1 || !address) {
      showErrorMessage({
        message: "Não foi possível cadastrar o endereço"
      });
      setSubmitting(false);
      return;
    }

    const vectorFocusService = new VectorFocusService();
    const resUpdateFocus = await vectorFocusService.updateVectorFocus({
      vectorFocusId: registeredVectorFocus.id,
      pictureId: registeredVectorFocus.picture_id,
      addressId: address.id,
      diseaseVectorId: registeredVectorFocus.disease_vector_id,
      denounceStatusId: registeredVectorFocus.denounce_status_id,
      description: registeredVectorFocus.description,
      possibleFocusId: registeredVectorFocus.possible_focus_id
    });

    if (resUpdateFocus.status !== 1) {
      showErrorMessage({
        message: "Não foi possível atualizar o novo foco com o endereço"
      });
      setSubmitting(false);
      return;
    }

    setOpenInfoDialogue({
      open: true,
      msgId: INFO_DIALOGUE_MSGS.VECTOR_FOCUS_HAS_BEEN_CREATED,
      callback: () => navigate(-1)
    });
    setSubmitting(false);
  }

  return (
    <div className="inner-page">
      <Header
        showBackBtn
        hideBackBtnLabel
        pageTitle="Novo Foco"
      />
      <div className="inner-page-content-container">
        <CameraCapture setSelectedFile={setVectorFocusImageFile}/>
        <CreateVectorFocusForm
          diseaseVectors={diseaseVectors}
          possibleVectorFocus={possibleVectorFocus}
          onSubmit={handleSubmitVectorFocusForm} />
        {
          showAddressForm && (
            <div className="confirm-address-container">
              <p className="section-title">Agora confirme o endereço</p>
              <AddressForm
                onSubmit={handleSubmitAddressForm}
                city={agentCity}
                neighborhood={''}
                street={''}
                number={''}
                complement={''}
                zipCode={''}/>
            </div>
          )
        }
        <InformationDialogue
          title={infoMsgTitle}
          confirmLabel={infoMsgLabel}
          open={openInfoDialogue}
          onClose={handleCloseInfoDialogue}
        >
          {infoMsgContent}
        </InformationDialogue>
      </div>
      <BottomMenu />
    </div>
  );
}

export default VectorFocusCreate;
